import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router'
import { RiTrophyLine } from 'react-icons/ri';
import Image from 'next/image'

import collocations from '../../components/collocation-panda/collocations';
import Hint from '../../components/collocation-panda/Hint';
import Info from '../../components/collocation-panda/Info'
import { useUser } from '../../hooks/usePandaUser';
import PlayerRank from '../../components/collocation-panda/PlayerRank';


const CollocationGame = ({ }) => {

    const router = useRouter()
    const userId = useUser();

    const [currentWord, setCurrentWord] = useState('');
    const [guessedWords, setGuessedWords] = useState<Array<{ word: string; position: number }>>([]);
    const [example, setExample] = useState('');
    const [chain, setChain] = useState<string[]>([]);
    const [attempts, setAttempts] = useState(0);
    const [currentGuess, setCurrentGuess] = useState('');
    const [feedback, setFeedback] = useState('');
    const [showTrophy, setShowTrophy] = useState(false);
    const [revealedChars, setRevealedChars] = useState(1);

    const word = router.query.word as string

    const revealChar = (word: string, revealedChars: number) => {
        if (revealedChars >= word.length) return word
        return word.slice(0, revealedChars) + '.'.repeat(word.length - revealedChars);
    }

    useEffect(() => {
        let initialCurrentWord = Object.keys(collocations)[Math.floor(Math.random() * Object.keys(collocations).length)];
        if (word && Object.keys(collocations).includes(word)) {
            initialCurrentWord = word;
        }
        setCurrentWord(initialCurrentWord)
        setChain([initialCurrentWord])
        setExample(randomExample(initialCurrentWord))
    }, [word])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const shortForm = currentGuess.trim().toLowerCase().replace(currentWord, '').trim();
        const position = collocations[currentWord].indexOf(shortForm);

        if (guessedWords.some(item => item.word === shortForm)) {
            setFeedback(`😥 Oops! You've already guessed "${currentGuess}". Try something different.`);
            setCurrentGuess('');
            return;
        }

        setAttempts(attempts + 1)

        if (position !== -1) {
            setFeedback(`🎉 Hurray! You've nailed it. Collocation "${currentWord} ${shortForm}" is at position ${position + 1}. Keep going!`);
            setGuessedWords((prev) => [...prev, { word: shortForm, position: position + 1 }]);

            await fetch('/api/panda/updatePlayerStats', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: userId,
                    roundsWon: 1,
                    movesMade: attempts + 1,
                }),
            });
        } else {
            setFeedback(`🙁 Oh no! "${shortForm}" is not the most frequent collocation with "${currentWord}". Give it another shot.`);
            setGuessedWords((prev) => [...prev, { word: shortForm, position: 1000 }]);
            setRevealedChars(revealedChars + 1);
        }
        setCurrentGuess('');
    };

    useEffect(() => {
        if (guessedWords.filter(w => w.position <= 5).length === 5) {
            setFeedback(`🏆🎉🏆 Congratulations, you are a Collocation Master! You guessed all 5 most common collocations within just ${attempts} attempts! 🥳👏`);
            setShowTrophy(true);
        }
    }, [guessedWords])

    const getHints = (word: string) => {
        // Generate hints for each word
        const hints = [
            `words: ${word.split(' ').length}`,
            `"${word.slice(0, 2)}..."`,
            `"...${word.slice(-2)}"`,
        ];
        return hints.join(", ");
    };

    const randomExample = (currentWord: string) => {
        if (!currentWord) return ''
        const randomIndex = 5 + Math.floor(Math.random() * (collocations[currentWord].length - 5));
        return collocations[currentWord][randomIndex];
    };

    return (

        <div>
            <Head>
                <title>
                    Collocation Panda - A Fun Game to Learn Collocations for IELTS Writing Preparation
                </title>
                <meta
                    name="description"
                    content="Learn IELTS collocations with Collocation Panda, a free game that helps you memorize the most frequent collocations for IELTS Writing Task 2. Improve your writing skills today!"
                />
            </Head>

            <div className="flex flex-col items-center justify-start px-4 pb-24 bg-blue-900 min-h-screen">

                <div className="flex flex-col items-center mb-6 text-center">
                    <Image className="-mb-8 -mt-1 -ml-2" src="/collocation-panda-transparent.png" width={120} height={120} alt="Collocation Panda Logo" />
                    <h1 className="text-3xl font-bold text-white uppercase"><small className="text-sm">Collocation</small> <br />Panda</h1>
                </div>
                <p className="w-full max-w-lg mb-6 text-sm text-center text-white">
                    Welcome to Collocation Panda Game! Your goal is to guess the 5 most common collocations for a given word with the least attempts. Collocations are groups of words that are often used together.
                </p>
                <PlayerRank userId={userId} />
                <form onSubmit={handleSubmit} className="w-full max-w-lg mb-6 p-6 bg-blue-500 rounded-lg">
                    <p className="text-lg text-center text-white mb-4">
                        Complete the collocation: <b className='uppercase'>"{currentWord}  ___"</b>
                    </p>
                    <div className="flex flex-col items-center">
                        <input
                            type="text"
                            value={currentGuess}
                            onChange={(e) => setCurrentGuess(e.target.value)}
                            className="w-full max-w-md py-2 px-4 mb-4 bg-white text-black rounded-lg"
                            placeholder={`Example: "${currentWord} ${example}"`}
                        />
                        <button type="submit" className="py-2 px-4 bg-gray-900 text-white font-bold uppercase rounded-lg">
                            SEND ANSWER
                        </button>
                    </div>
                </form>

                {feedback &&
                    <div className={`w-full rounded-md max-w-lg mb-6 flex items-center ${showTrophy ? "bg-green-500" : feedback.startsWith('🎉') ? "bg-green-500" : feedback.startsWith('😥') ? "bg-yellow-500" : "bg-red-400"}`}>
                        {showTrophy && <RiTrophyLine className="w-20 h-20 ml-4 mr-4 text-yellow-400 rounded-lg" />}
                        <p className={`p-2 text-sm font-bold uppercase ${showTrophy ? "text-white" : feedback.startsWith('🎉') ? "text-white" : feedback.startsWith('😥') ? "text-black" : "text-white"}`}>
                            {feedback}
                        </p>
                    </div>
                }

                <div className="w-full max-w-lg mb-6 p-4 border border-blue-500 rounded-lg">
                    <p className="text-xl font-bold text-white mb-2">Attempts: {attempts}</p>
                </div>
                <div className="w-full max-w-lg flex flex-row items-start justify-between border-blue-500 border rounded-lg">
                    <div className="w-1/2 mb-6 md:mb-0 p-4 mr-0 md:mr-4">
                        <p className="text-lg text-gray-100 mb-2">Guessed Words:</p>
                        <ul className="list-inside text-lg font-bold text-yellow-400">
                            {collocations[currentWord]?.slice(0, 5).map((word, index) => {
                                const guessed = guessedWords.find(item => item.position == index + 1 && item.word == word)
                                if (guessed) {
                                    return (
                                        <li key={index}>
                                            {index + 1}. {word}
                                        </li>
                                    )
                                }
                                return (
                                    <li key={index}>
                                        {index + 1}. {revealChar(word, revealedChars)}{" "}

                                        <Hint hint={getHints(word)} />
                                    </li>
                                )
                            }
                            )}
                        </ul>
                    </div>
                    <div className="w-1/2 p-4">
                        <p className="text-lg text-gray-100 mb-2">Attempted Words:</p>
                        <ul className="list-inside text-lg text-white">
                            {guessedWords
                                .sort((a, b) => a.position - b.position)
                                .filter(({ position }) => position > 5)
                                .map((item, index) => (
                                    <li key={index}>
                                        {item.position <= 1000 ? `${item.position}.` : '1000+.'} {currentWord} {item.word}
                                    </li>
                                ))}
                        </ul>
                    </div>
                </div>
                <div className="mt-6">
                    <a className="py-2 px-4 bg-blue-800 text-white font-bold uppercase rounded-lg" href='/collocation-panda'>Next Round</a>
                </div>
            </div >
            <Info />
        </div>
    );
};

export default CollocationGame;
